import { motion } from "framer-motion";
import { Smartphone, Bell, Camera, MapPin, Clock, CheckCircle, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const stagger = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const FEATURES = [
  {
    icon: Smartphone,
    title: "Real-Time Updates",
    desc: "Track your car's progress from anywhere on your phone. No more wondering or waiting for callbacks.",
  },
  {
    icon: Camera,
    title: "Photo Evidence",
    desc: "See actual photos of your vehicle at each stage. Watch the transformation happen in real-time.",
  },
  {
    icon: Bell,
    title: "Instant Notifications",
    desc: "Get email alerts when your car moves to the next stage. You'll always know exactly where things are at.",
  },
  {
    icon: Clock,
    title: "Stage-by-Stage Progress",
    desc: "From bodywork to final polish, follow every step of the process with clear stage indicators.",
  },
];

const STAGES = [
  { name: "Repairs", status: "completed" },
  { name: "Priming", status: "completed" },
  { name: "Painted", status: "current" },
  { name: "Assembled", status: "pending" },
  { name: "Polished", status: "pending" },
  { name: "Ready", status: "pending" },
];

export const TrackJobShowcase = () => {
  const navigate = useNavigate();

  return (
    <section id="track-feature" data-testid="track-feature-section" className="py-24 md:py-32 bg-[#050505] relative overflow-hidden">
      {/* Background accent */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-[#3b82f6] blur-[150px]" />
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <motion.div {...fadeUp} className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-[1px] bg-[#3b82f6]" />
            <span className="text-sm tracking-[0.3em] uppercase text-zinc-500 font-body font-medium">Why Choose Us</span>
          </div>
          <h2 data-testid="track-feature-heading" className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight text-white leading-[0.95] mb-6">
            Track Your Car's<br />
            <span className="text-[#3b82f6]">Transformation</span>
          </h2>
          <p className="text-zinc-400 font-body text-base md:text-lg max-w-2xl">
            No more guessing. No more phone tag. With our live job tracker, you'll see exactly what's happening with your vehicle every step of the way.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Features */}
          <div className="space-y-6">
            {FEATURES.map((feature, i) => (
              <motion.div
                key={feature.title}
                {...stagger}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="flex gap-5 group"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-white/5 border border-white/10 flex items-center justify-center group-hover:border-[#3b82f6]/50 group-hover:bg-[#3b82f6]/10 transition-all duration-300">
                  <feature.icon size={22} className="text-[#3b82f6]" />
                </div>
                <div>
                  <h3 className="font-heading text-lg uppercase tracking-wide text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-zinc-500 font-body text-sm leading-relaxed">
                    {feature.desc}
                  </p>
                </div>
              </motion.div>
            ))}

            {/* CTA */}
            <motion.div {...stagger} transition={{ duration: 0.4, delay: 0.5 }} className="pt-4">
              <button
                onClick={() => navigate("/track")}
                data-testid="track-cta-btn"
                className="group flex items-center gap-3 px-6 py-3 bg-[#3b82f6] text-white font-heading text-sm tracking-[0.15em] uppercase hover:bg-[#3b82f6]/80 transition-all duration-300"
              >
                Track Your Job
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>
          </div>

          {/* Right: Mock Phone/Progress Preview */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative"
          >
            {/* Phone mockup */}
            <div className="relative mx-auto max-w-[320px]">
              {/* Phone frame */}
              <div className="bg-[#111] border-2 border-white/10 rounded-[2.5rem] p-3 shadow-2xl">
                {/* Screen */}
                <div className="bg-[#0a0a0a] rounded-[2rem] overflow-hidden">
                  {/* Status bar */}
                  <div className="h-8 bg-[#0a0a0a] flex items-center justify-center">
                    <div className="w-20 h-5 bg-black rounded-full" />
                  </div>

                  {/* App content */}
                  <div className="px-5 pb-8 pt-4">
                    {/* Header */}
                    <div className="text-center mb-6">
                      <p className="text-[10px] tracking-[0.2em] uppercase text-[#3b82f6] mb-1">Live Tracking</p>
                      <h4 className="font-heading text-lg uppercase text-white tracking-wide">Nissan Silvia S15</h4>
                      <p className="text-zinc-600 text-xs mt-1">Full Respray</p>
                    </div>

                    {/* Progress indicator */}
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] uppercase tracking-wider text-zinc-500">Progress</span>
                        <span className="text-[#22c55e] text-xs font-medium">3/6 Stages</span>
                      </div>
                      <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                        <motion.div
                          className="h-full bg-gradient-to-r from-[#3b82f6] to-[#22c55e]"
                          initial={{ width: 0 }}
                          whileInView={{ width: "50%" }}
                          viewport={{ once: true }}
                          transition={{ duration: 1, delay: 0.5 }}
                        />
                      </div>
                    </div>

                    {/* Stages */}
                    <div className="space-y-3">
                      {STAGES.map((stage, i) => (
                        <div
                          key={stage.name}
                          className={`flex items-center gap-3 py-2 px-3 rounded-lg ${
                            stage.status === "current"
                              ? "bg-[#3b82f6]/10 border border-[#3b82f6]/30"
                              : stage.status === "completed"
                              ? "bg-white/5"
                              : "opacity-40"
                          }`}
                        >
                          <div
                            className={`w-5 h-5 rounded-full flex items-center justify-center ${
                              stage.status === "completed"
                                ? "bg-[#22c55e]"
                                : stage.status === "current"
                                ? "bg-[#3b82f6] animate-pulse"
                                : "bg-white/10"
                            }`}
                          >
                            {stage.status === "completed" ? (
                              <CheckCircle size={12} className="text-black" />
                            ) : stage.status === "current" ? (
                              <div className="w-2 h-2 bg-white rounded-full" />
                            ) : (
                              <div className="w-2 h-2 bg-white/30 rounded-full" />
                            )}
                          </div>
                          <span
                            className={`text-xs font-medium ${
                              stage.status === "current"
                                ? "text-[#3b82f6]"
                                : stage.status === "completed"
                                ? "text-white"
                                : "text-zinc-600"
                            }`}
                          >
                            {stage.name}
                          </span>
                          {stage.status === "current" && (
                            <span className="ml-auto text-[10px] uppercase tracking-wider text-[#3b82f6] bg-[#3b82f6]/20 px-2 py-0.5 rounded">
                              Now
                            </span>
                          )}
                          {stage.status === "completed" && (
                            <Camera size={12} className="ml-auto text-zinc-500" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating notification */}
              <motion.div
                initial={{ opacity: 0, x: 50, y: 20 }}
                whileInView={{ opacity: 1, x: 0, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 1 }}
                className="absolute -right-4 top-1/4 bg-[#111] border border-white/10 rounded-lg p-3 shadow-xl max-w-[180px]"
              >
                <div className="flex items-start gap-2">
                  <div className="w-8 h-8 bg-[#22c55e]/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Bell size={14} className="text-[#22c55e]" />
                  </div>
                  <div>
                    <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Update</p>
                    <p className="text-xs text-white font-medium">Paint stage complete!</p>
                  </div>
                </div>
              </motion.div>

              {/* Floating photo indicator */}
              <motion.div
                initial={{ opacity: 0, x: -50, y: -20 }}
                whileInView={{ opacity: 1, x: 0, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 1.2 }}
                className="absolute -left-4 bottom-1/4 bg-[#111] border border-white/10 rounded-lg p-3 shadow-xl"
              >
                <div className="flex items-center gap-2">
                  <Camera size={14} className="text-[#3b82f6]" />
                  <p className="text-xs text-white">3 new photos</p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Bottom trust badges */}
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-20 pt-12 border-t border-white/5"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "100%", label: "Transparency" },
              { value: "24/7", label: "Access" },
              { value: "Live", label: "Updates" },
              { value: "Free", label: "To Use" },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="font-heading text-2xl md:text-3xl text-white mb-1">{stat.value}</p>
                <p className="text-xs uppercase tracking-[0.2em] text-zinc-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TrackJobShowcase;
